
import { Options, Vue } from 'vue-class-component';
import { ItemsActionNames } from '@/definitions/app/item.actions.name';
import { viewModelRepository } from '@/api/common';
import { autoUpdateHelper } from '@/api/common/auto-update-helper';
import { aclModule } from '@/store/acl';
import { configModule } from '@/store/config';
import { dataModule } from '@/store/data';
import { actionHandler } from '@/store/data/ActionHandler';
import { multisidebarModule } from '@/store/multisidebar';
import { MultisidebarItemTypes } from '@/store/multisidebar/types';
import NLoadingCircle from '@/uikit/loading/NLoadingCircle.vue';
import NTable from '@/uikit/table/NTable.vue';
import { ITableCellChangePayload } from '@/uikit/table/NTableCell.vue';
import InfiniteScroll from '@/components/common/InfiniteScroll.vue';
import SettingsBar from '@/components/common/SettingsBar.vue';
import SettingsPageLayout from '@/pages/settings/SettingsPageLayout.vue';
import { ADColumn, mainColumns, metaColumns } from './users-table-schema';

const ActionPathNames = {
  RealName: 'real_name',
  Group: 'primary_group',
  Active: 'active'
};

@Options({
  components: {
    InfiniteScroll,
    NLoadingCircle,
    NTable,
    SettingsBar,
    SettingsPageLayout
  }
})
export default class UsersPage extends Vue {
  get module() {
    const module = viewModelRepository.getUsersListViewModel();
    module.aclModelName = 'user';
    module.getStatistics();
    return module;
  }

  get items() {
    return this.module.items;
  }

  get isADEnabled() {
    return configModule.config.services.ffsecurity.active_directory;
  }

  get columns() {
    let columns = [...mainColumns];
    if (this.isADEnabled) columns.push(ADColumn);
    columns.push(...metaColumns);
    return columns;
  }

  get modelAcl() {
    return { ...aclModule.getModelAcl(this.module), delete: this.isLoggedInUserAdmin, add: this.isLoggedInUserAdmin };
  }

  get isLoggedInUserAdmin() {
    return dataModule.isCurrentUserAdmin;
  }

  get groups() {
    return dataModule.groupsModule.items;
  }

  get sidebarType() {
    return MultisidebarItemTypes.Users;
  }

  get sidebarModule() {
    return multisidebarModule;
  }

  get selectedItemIds(): number[] {
    return this.sidebarModule.items.filter((v) => v.type === this.sidebarType).map((v) => v.model.item?.id);
  }

  handleSelectChanges(changes: number[]) {
    changes
      ?.map((id) => (this.module.items as any[]).find((v: any) => v.id === id))
      .forEach((item) => actionHandler.run(ItemsActionNames.ToggleSelectItem, { type: this.sidebarType, rawItem: item }));
  }

  async handleTableAction(payload: ITableCellChangePayload) {
    if (payload.type === 'change') {
      const item = { type: MultisidebarItemTypes.Users, rawItem: payload.row };
      const isActive = payload.row.active;

      switch (payload.path) {
        case ActionPathNames.Active:
          await actionHandler.run(isActive ? ItemsActionNames.Activate : ItemsActionNames.Deactivate, item);
          break;
        case ActionPathNames.RealName:
          await actionHandler.run(ItemsActionNames.ShowItem, item);
          break;
        case ActionPathNames.Group:
          await actionHandler.run(ItemsActionNames.ShowItem, { type: MultisidebarItemTypes.Groups, rawItem: payload.row.primary_group });
          break;
      }
    }
  }

  handleSearch(query: string) {
    if (this.module.filter.current.real_name_contains !== query) {
      this.module.filter.current.real_name_contains = query;
      this.module.get();
    }
  }

  handleTableSort(columnName: string) {
    const ordering = this.module.filter.current.ordering === columnName ? `-${columnName}` : columnName;
    this.module.filter.current.ordering = ordering;
    this.module.get();
  }

  async handleCreate() {
    const newMsbItem = await this.sidebarModule.addNewItemByType(this.sidebarType);
    const user = newMsbItem.model.item;
    if (!user.primary_group && this.groups.length) {
      user.primary_group = this.groups[this.groups.length - 1]?.id;
    }
  }

  mounted() {
    this.module.get();
    autoUpdateHelper.addListInstance(this.module);
  }
}
